import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ExternalLink, Phone, Mail, Navigation, Star } from "lucide-react";
import { SectionHeader } from "./SectionHeader";
import { useT } from "@/lib/i18n";
import { mapsEmbedUrl } from "@/lib/siteContent";

/**
 * Locations hub and map in one section: the cards list every place under the
 * Chaty Mara umbrella, and the block below them pins down where the current
 * one actually is. Adding a location = one entry in
 * content.*.locationsSection.cards (+ its landing page via seoRoutes.json).
 */
export const Location = () => {
  const { t, lang } = useT();
  const cards = t.locationsSection?.cards ?? [];
  // A single location in a three-column row leaves two thirds of the section
  // empty, so on its own it sits beside the heading instead of under it. From
  // two locations up, the cards fill a row by themselves and go back below.
  const solo = cards.length === 1;

  return (
    <section id="lokalita" className="section-y bg-secondary/40" aria-labelledby="lokalita-title">
      <div className="container-x">
        <div className={solo ? "grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" : ""}>
          <SectionHeader
            kicker={t.locationsSection?.kicker}
            title={t.locationsSection?.title}
            subtitle={t.locationsSection?.subtitle}
          />

        {cards.length > 0 && (
          <div
            id="lokality"
            className={`grid gap-6 ${solo ? "grid-cols-1 mt-0" : "mt-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}
            data-testid="locations-grid"
          >
            {cards.map((c, idx) => (
              <motion.a
                key={c.href}
                href={c.href}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: idx * 0.08 }}
                className="group rounded-2xl border border-border bg-card shadow-soft overflow-hidden hover:-translate-y-0.5 hover:shadow-lifted hover:border-foreground/10 transition-[box-shadow,transform,border-color] duration-200 flex flex-col"
                data-testid={`location-card-${idx}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    width="880"
                    height="550"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
                  />
                  <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-xs font-medium text-foreground shadow-soft">
                    <MapPin className="h-3.5 w-3.5" />
                    {c.badge}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className={`font-display font-semibold text-foreground ${solo ? "text-2xl" : "text-xl"}`}>
                    {c.name}
                  </h3>
                  <p
                    className={`mt-2 text-foreground/80 leading-relaxed flex-1 ${solo ? "text-base" : "text-sm"}`}
                  >
                    {c.body}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground">
                    {c.linkLabel}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        )}
        </div>

        {/* Same section, quieter heading: the map answers "where exactly", which
            only makes sense once the cards have said "which places". */}
        <div className={`${cards.length > 0 ? "mt-14 border-t border-border/60 pt-12" : "mt-10"}`}>
          <div className="max-w-2xl">
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              {t.location.title}
            </h3>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              {t.location.subtitle}
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            <div
              className="lg:col-span-3 rounded-2xl overflow-hidden border border-border shadow-soft bg-secondary/40"
              data-testid="map-embed"
            >
              <iframe
                title={`${t.brand.name}, mapa`}
                src={mapsEmbedUrl(lang, t.brand.name)}
                className="block w-full h-[320px] sm:h-[420px] lg:h-full lg:min-h-[460px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}>
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="kicker">{t.location.addressLabel}</div>
                    <address className="not-italic mt-1 text-foreground text-sm leading-relaxed">
                      {t.brand.address.line1}<br />
                      {t.brand.address.city}<br />
                      {t.brand.address.country}
                    </address>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <a
                        href={t.brand.directionsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-primary-foreground shadow-soft hover:opacity-90 transition-opacity"
                        style={{ background: "hsl(var(--pine))" }}
                        data-testid="location-directions-link"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                        {t.ui.directions}
                      </a>
                      <a
                        href={t.brand.mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary/60 transition-colors"
                        data-testid="location-gbp-link"
                      >
                        <Star className="h-3.5 w-3.5" />
                        {t.ui.viewOnGoogle}
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <div className="kicker">{t.location.contactLabel}</div>
                <div className="mt-3 space-y-3 text-sm">
                  <a href={t.brand.phoneHref} className="flex items-center gap-2 text-foreground hover:text-primary" data-testid="location-phone">
                    <Phone className="h-4 w-4" /> {t.brand.phone}
                  </a>
                  <a href={t.brand.emailHref} className="flex items-center gap-2 text-foreground hover:text-primary" data-testid="location-email">
                    <Mail className="h-4 w-4" /> {t.brand.email}
                  </a>
                </div>
              </div>

              <div className="rounded-2xl p-6 shadow-soft text-primary-foreground" style={{ background: "hsl(var(--pine))" }}>
                <div className="kicker-on-dark">{t.location.distancesLabel}</div>
                <ul className="mt-2 text-sm space-y-1.5 text-white/95">
                  {t.location.distances.map((d, i) => (
                    <li key={i}>{d.name}: <strong>{d.value}</strong></li>
                  ))}
                </ul>
                {t.location.maraLinkHref && (
                  <a
                    href={t.location.maraLinkHref}
                    className="mt-4 inline-block text-sm font-medium text-white underline decoration-white/40 underline-offset-4 hover:decoration-white"
                    data-testid="location-mara-link"
                  >
                    {t.location.maraLinkLabel}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
